import { useEffect } from 'react';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router';

import { Button } from 'ui';
import { Footer } from '../../components/Footer/Footer';
import { HeaderError } from '../../components/Header/HeaderError';

import logo from './../../assets/images/Stand_Logo_White_Black 1.png';
import {
  CABINET_ERROR_PAGES,
  resolveCabinetErrorCode,
  type CabinetErrorCode,
} from './errorCatalog';
import styles from './Error.module.scss';

type ErrorViewProps = {
  code?: CabinetErrorCode;
  onRetry?: () => void;
};

export const ErrorView = ({ code = 404, onRetry }: ErrorViewProps) => {
  const navigate = useNavigate();
  const copy = CABINET_ERROR_PAGES[code];

  useEffect(() => {
    const previous = document.title;
    document.title = `${copy.title} | MyTeacher`;
    return () => {
      document.title = previous;
    };
  }, [copy.title]);

  return (
    <div className={styles.content}>
      <div className={styles.head}>
        <img src={logo} alt='' />
        <h1>{copy.title}</h1>
      </div>

      <p className={styles.description}>{copy.description}</p>

      <div className={styles.warnings}>
        <div className={styles.warning}>
          <h2>{copy.happenedTitle}</h2>
          <p>{copy.happened}</p>
        </div>
        <div className={styles.warning}>
          <h2>{copy.fixTitle}</h2>
          <p>{copy.fix}</p>
        </div>
      </div>

      <div className={styles.actions}>
        <Button
          variant='filled'
          shape='brick'
          size='lg'
          onClick={() => navigate('/')}
        >
          {copy.homeLabel}
        </Button>
        {copy.loginLabel ? (
          <Button
            variant='outlined'
            shape='brick'
            size='lg'
            onClick={() => navigate('/login')}
          >
            {copy.loginLabel}
          </Button>
        ) : null}
        {onRetry ? (
          <Button variant='outlined' shape='brick' size='lg' onClick={onRetry}>
            Обновить страницу
          </Button>
        ) : (
          <Button variant='outlined' shape='brick' size='lg' onClick={() => navigate(-1)}>
            Назад
          </Button>
        )}
      </div>
    </div>
  );
};

type ErrorPageProps = {
  code?: CabinetErrorCode;
  embedded?: boolean;
  compact?: boolean;
  onRetry?: () => void;
};

export const ErrorPage = ({
  code = 404,
  embedded = false,
  compact = false,
  onRetry,
}: ErrorPageProps) => {
  const inner = (
    <main className={`${styles.error}${compact ? ` ${styles.compact}` : ''}`}>
      <ErrorView code={code} onRetry={onRetry} />
    </main>
  );

  if (embedded) {
    return inner;
  }

  return (
    <div className={styles.standalone}>
      <HeaderError />
      {inner}
      <Footer />
    </div>
  );
};

export const RouteErrorPage = () => {
  const error = useRouteError();
  let code: CabinetErrorCode = 500;
  let retry: (() => void) | undefined;

  if (isRouteErrorResponse(error)) {
    code = resolveCabinetErrorCode(error.status);
  } else {
    retry = () => window.location.reload();
  }

  return <ErrorPage code={code} onRetry={retry} />;
};
